import React, { useContext, useState } from "react";
import PropTypes from "prop-types";

import { Button, Header } from "semantic-ui-react";

import CRMPermissionWarning from "scenes/Hubly/components/Workspace/components/CRMPermissionWarning";
import { ToasterContext } from "scenes/Hubly/components/Workspace/Provider";

import { useTotalMembers } from "./hooks";
import { MembersList } from "..";
import AddClientSelector from "../AddClientSelector";
import HouseholdConfirmationModal from "../HouseholdConfirmationModal";

export function MembersSection({
  obj,
  addMember,
  bulkAddMember,
  removeMember,
  members,
  type,
  hasIntegration,
  showHouseholdConfirm,
  onDisableHouseholdConfirm,
  onViewClick,
}) {
  const { fireSuccess } = useContext(ToasterContext);
  const [adding, setAdding] = useState(false);

  const [loading, setLoading] = useState(false);
  const [clientWithHouseHold, setClientWithHousehold] = useState(null);

  const totalMembers = useTotalMembers(obj?.id, type);

  const addClient = (clientId) => {
    setClientWithHousehold(null);
    setLoading(true);
    addMember(clientId, obj).then(() => {
      fireSuccess(`Successfully added ${type}`);
      setLoading(false);
      members.setModified();
      members.refetch();
    });
  };

  return (
    <React.Fragment>
      <Header as="h5" style={{ marginBottom: "0.5em", display: "flex" }}>
        Members ({members.totalCount})
        {hasIntegration && members.totalCount < totalMembers && (
          <CRMPermissionWarning permissionSubject={`members of this ${type}`} />
        )}
      </Header>
      <React.Fragment>
        <div style={{ paddingBottom: "1em" }}>
          {!adding ? (
            <Button
              basic
              loading={loading}
              disabled={loading}
              compact
              content="Add"
              onClick={() => {
                setAdding(true);
              }}
            />
          ) : (
            <AddClientSelector
              loading={loading || members.loading}
              members={members.data}
              onAdd={(client) => {
                if (client.householdId && showHouseholdConfirm) {
                  setClientWithHousehold(client);
                } else {
                  addClient(client.id);
                }
              }}
              onCancel={setAdding}
            />
          )}
        </div>
        <MembersList
          members={members.data}
          loading={members.loading}
          showLoadMore={members.hasMorePages}
          loadMoreData={members.readNext}
          type={type}
          onViewClick={onViewClick}
          onDeleteClick={(clientId) => {
            setLoading(true);
            removeMember(clientId, obj.id).then(() => {
              fireSuccess(`Successfully removed ${type}`);
              setLoading(false);
              members.setModified();
              members.refetch();
            });
          }}
        />
      </React.Fragment>

      {clientWithHouseHold && (
        <HouseholdConfirmationModal
          type={type}
          onDisableHouseholdConfirm={onDisableHouseholdConfirm}
          client={clientWithHouseHold}
          onAddClient={addClient}
          onBulkAddClients={(clientId) => {
            setClientWithHousehold(null);
            setLoading(true);
            bulkAddMember(clientId, obj).then(() => {
              fireSuccess(`Successfully added all clients to ${type}`);
              setLoading(false);
              members.setModified();
              members.refetch();
            });
          }}
          onClose={setClientWithHousehold}
        />
      )}
    </React.Fragment>
  );
}

MembersSection.defaultProps = {
  showHouseholdConfirm: true,
  onDisableHouseholdConfirm: () => {},
};

MembersSection.propTypes = {
  obj: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  hasIntegration: PropTypes.bool.isRequired,
  members: PropTypes.object.isRequired,
  addMember: PropTypes.func.isRequired,
  bulkAddMember: PropTypes.func.isRequired,
  removeMember: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  showHouseholdConfirm: PropTypes.bool,
  onDisableHouseholdConfirm: PropTypes.func,
};

export default MembersSection;
